import type { ReactNode, RefObject } from 'react';
import { createContext, useRef, useState } from 'react';

import { isSameDay } from '@repo/common';

import type { CarouselRef } from '../../../components/elements/Carousel/Carousel';
import { getPeriodLabel } from '../../../utils/time/month-time';
import {
  getNextPeriod,
  getPeriodDays,
  getPreviousPeriod,
  getStartOfPeriod,
  getWeekDays,
} from '../../../utils/time/week-time';

export interface CalendarContextProps {
  currentPeriodDays: Date[][];
  /** Start of the month or year on display. The week view moves by `currentPeriodIndex` instead. */
  currentPeriod: Date;
  selectedDay: Date;
  periodLabel: string;
  selectDay: (day: Date) => void;
  goToPreviousPeriod: () => void;
  goToNextPeriod: () => void;
  setCurrentPeriod: (period: Date) => void;
  onActiveIndexChange: (index: number) => void;
  carouselRef: RefObject<CarouselRef | null>;
  currentPeriodIndex: number;
}

export const CalendarContext = createContext<CalendarContextProps | undefined>(undefined);
interface CalendarContextProviderProps {
  children: ReactNode;
  periodType: 'week' | 'month' | 'year';
  initialDate?: Date;
  /** Called when the month or year view moves to another period; the week view does not report. */
  onPeriodChange?: (period: Date) => void;
}
export function CalendarContextProvider({
  children,
  periodType,
  initialDate,
  onPeriodChange,
}: CalendarContextProviderProps) {
  const initialDateKey = initialDate?.getTime() ?? 'today';
  return (
    <CalendarContextProviderContent
      key={`${periodType}:${initialDateKey}`}
      periodType={periodType}
      initialDate={initialDate}
      onPeriodChange={onPeriodChange}
    >
      {children}
    </CalendarContextProviderContent>
  );
}

function CalendarContextProviderContent({
  children,
  periodType,
  initialDate,
  onPeriodChange,
}: CalendarContextProviderProps) {
  const [today] = useState(() => initialDate ?? new Date());
  const [selectedDay, setSelectedDay] = useState(today);
  const initialPeriod = getStartOfPeriod(today, periodType);
  const carouselRef = useRef<CarouselRef>(null);

  // For week view, maintain a sliding window of weeks
  const [weeks, setWeeks] = useState(() => {
    if (periodType === 'week') {
      return [
        getWeekDays(getPreviousPeriod(initialPeriod, 'week')),
        getWeekDays(initialPeriod),
        getWeekDays(getNextPeriod(initialPeriod, 'week')),
      ];
    }
    return [];
  });

  const [currentPeriod, setCurrentPeriod] = useState(initialPeriod);
  const [currentPeriodIndex, setCurrentPeriodIndex] = useState(periodType === 'week' ? 1 : 0);
  const currentPeriodDays = periodType === 'week' ? weeks : getPeriodDays(currentPeriod, periodType);
  const monthPeriodIndex =
    periodType === 'month'
      ? currentPeriodDays.findIndex(week => week.some(day => isSameDay(day, selectedDay)))
      : currentPeriodIndex;
  const effectiveCurrentPeriodIndex = monthPeriodIndex >= 0 ? monthPeriodIndex : 0;
  const labelPeriod = periodType === 'week' ? (weeks[currentPeriodIndex]?.[0] ?? currentPeriod) : currentPeriod;
  const periodLabel = getPeriodLabel(labelPeriod, periodType);

  const handleSlideToIndex = (index: number) => {
    if (carouselRef.current) {
      carouselRef.current.slideTo(index);
    }
  };

  const handleWeekActiveIndexChange = (index: number) => {
    if (index === 0) {
      const previousWeek = getPreviousPeriod(weeks[0][0], 'week');
      setWeeks(previousWeeks => [getWeekDays(previousWeek), ...previousWeeks]);
      setCurrentPeriodIndex(1);
      return;
    }
    if (index === weeks.length - 1) {
      const nextWeek = getNextPeriod(weeks[weeks.length - 1][0], 'week');
      setWeeks(previousWeeks => [...previousWeeks, getWeekDays(nextWeek)]);
    }
    setCurrentPeriodIndex(index);
  };

  const changePeriod = (period: Date) => {
    setCurrentPeriod(period);
    onPeriodChange?.(period);
  };

  const goToPreviousPeriod = () => {
    if (periodType === 'week') {
      handleSlideToIndex(currentPeriodIndex - 1);
    } else {
      changePeriod(getPreviousPeriod(currentPeriod, periodType));
    }
  };

  const goToNextPeriod = () => {
    if (periodType === 'week') {
      handleSlideToIndex(currentPeriodIndex + 1);
    } else {
      changePeriod(getNextPeriod(currentPeriod, periodType));
    }
  };

  const contextValue = {
    carouselRef,
    currentPeriodDays,
    currentPeriod,
    selectedDay,
    periodLabel,
    selectDay: setSelectedDay,
    goToPreviousPeriod,
    goToNextPeriod,
    setCurrentPeriod: changePeriod,
    currentPeriodIndex: effectiveCurrentPeriodIndex,
    onActiveIndexChange: periodType === 'week' ? handleWeekActiveIndexChange : handleSlideToIndex,
  };

  return <CalendarContext.Provider value={contextValue}>{children}</CalendarContext.Provider>;
}
